'use client'

import { memo } from 'react'
import { useTranslations } from '@repo/core/i18n'
import { BuildingMultipleRegular, CalendarRegular, CallRegular, ChatRegular, ClockRegular, ComposeRegular, DeleteRegular, MailRegular, MoreVerticalRegular, TagRegular, TranslateRegular } from '@fluentui/react-icons'
import { format } from 'date-fns'
import { cn } from '@repo/ui'
import {
  Button,
  Badge,
  UserAvatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Card,
  CardContent,
  CardHeader,
} from '@repo/ui'
import type { Customer } from '../../entities/customer'

interface CustomerProfileProps {
  customer: Customer
  onEdit?: () => void
  onDelete?: () => void
  onCall?: () => void
  onEmail?: () => void
  onChat?: () => void
  className?: string
}

export const CustomerProfile = memo(function CustomerProfile({
  customer,
  onEdit,
  onDelete,
  onCall,
  onEmail,
  onChat,
  className,
}: CustomerProfileProps) {
  const t = useTranslations('crm.customerProfile')

  const display = customer.displayName || customer.name

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="border-b bg-secondary/30">
        <div className="flex items-start gap-4">
          <UserAvatar src={customer.avatar} name={display} size="xl" />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-xl font-semibold">{display}</h2>
              {customer.segment && (
                <Badge variant="outline">{customer.segment}</Badge>
              )}
            </div>

            {customer.companyId && (
              <p className="mt-1 flex items-center gap-1 text-muted-foreground">
                <BuildingMultipleRegular className="h-4 w-4" />
                {customer.company || customer.companyId}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {onCall && customer.phone && (
                <Button variant="outline" size="sm" onClick={onCall}>
                  <CallRegular className="mr-2 h-4 w-4" />
                  {t('actions.call')}
                </Button>
              )}
              {onEmail && customer.email && (
                <Button variant="outline" size="sm" onClick={onEmail}>
                  <MailRegular className="mr-2 h-4 w-4" />
                  {t('actions.email')}
                </Button>
              )}
              {onChat && (
                <Button variant="outline" size="sm" onClick={onChat}>
                  {t('actions.chat')}
                </Button>
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm">
                <MoreVerticalRegular className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={onEdit}>
                  <ComposeRegular className="mr-2 h-4 w-4" />
                  {t('actions.edit')}
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onDelete} intent="danger">
                    <DeleteRegular className="mr-2 h-4 w-4" />
                    {t('actions.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="grid gap-6 p-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="font-semibold">{t('sections.contact')}</h3>

          <div className="space-y-3">
            {customer.email && (
              <div className="flex items-center gap-3">
                <MailRegular className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`mailto:${customer.email}`}
                  className="text-sm hover:underline"
                >
                  {customer.email}
                </a>
              </div>
            )}

            {customer.phone && (
              <div className="flex items-center gap-3">
                <CallRegular className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`tel:${customer.phone}`}
                  className="text-sm hover:underline"
                >
                  {customer.phone}
                </a>
              </div>
            )}

            {customer.language && (
              <div className="flex items-center gap-3">
                <TranslateRegular className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{customer.language}</span>
              </div>
            )}

            {customer.timezone && (
              <div className="flex items-center gap-3">
                <ClockRegular className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{customer.timezone}</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold">{t('sections.summary')}</h3>

          <div className="space-y-3">
            {typeof customer.interactionCount === 'number' && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ChatRegular className="h-4 w-4" />
                  {t('fields.interactionCount')}
                </span>
                <span className="font-medium">{customer.interactionCount}</span>
              </div>
            )}

            {typeof customer.score === 'number' && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('fields.score')}
                </span>
                <span className="font-medium">{customer.score}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarRegular className="h-4 w-4" />
                {t('fields.customerSince')}
              </span>
              <span className="font-medium">
                {format(new Date(customer.createdAt), 'MMM d, yyyy')}
              </span>
            </div>
          </div>
        </div>

        {customer.tags.length > 0 && (
          <div className="space-y-2 md:col-span-2">
            <h3 className="flex items-center gap-2 font-semibold">
              <TagRegular className="h-4 w-4" />
              {t('sections.tags')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {customer.tags.map((tag) => (
                <Badge key={tag} color="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {(customer.note || customer.notes) && (
          <div className="space-y-2 md:col-span-2">
            <h3 className="font-semibold">{t('sections.notes')}</h3>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {customer.note || customer.notes}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
})
